import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Lock, Utensils } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { PageHeader } from '@/components/core';
import { RadioGroup } from '@/components/ui-kit/radio-group';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { MEAL_TABS, MealTab } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useUrlTab } from '@/hooks/use-linked-record';
import { SoonBadge, SoonPanel } from '@/modules/settings';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { weekdayNameOf } from '@/lib/utils/custom-date/custom-date';
import { createRequestHistoryTableColumns } from '../../components/request-history-table/request-history-table-columns';
import { DEFAULT_MY_LUNCH_REQUESTS_ORDER } from '../../graphql/queries';
import {
  useCancelLunchRequest,
  useCurrentUserId,
  useGetMyLunchRequests,
  useLunchStatus,
  useMyWorkDays,
  useRequestLunch,
} from '../../hooks/use-lunch';
import { getTargetLunchDay, LunchRequestsPagingResult, LunchRequest } from '../../types/lunch.types';
import { MealHero } from '../../components/meal-hero/meal-hero';
import { MealOptionCard } from '../../components/meal-option-card/meal-option-card';
import { MealSlot } from '../../components/meal-option-card/meal-slot';
import { MealBar } from '../../components/meal-bar/meal-bar';
import { MealWeek } from '../../components/meal-week/meal-week';

const RequestTabSkeleton = () => (
  <div className="flex flex-col gap-6" aria-busy="true">
    {/* Hero banner skeleton */}
    <Skeleton className="h-44 w-full rounded-lg" />
    
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Skeleton className="h-44 w-full rounded-lg" />
          <Skeleton className="h-44 w-full rounded-lg" />
        </div>
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  </div>
);

const RequestTab = ({
  date,
  userRequests = [],
}: {
  date: string;
  userRequests?: LunchRequest[];
}) => {
  const { t } = useTranslation();
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const weekDay = weekdayNameOf(date) ?? '';

  const isLunchEnabled = useHasPermission(HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED);
  const canRequest = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_REQUEST);
  const canCancel = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_CANCEL);
  const { data: workDays, isLoading: isWorkDaysLoading } = useMyWorkDays(date);
  const isWorkDay = Boolean(workDays?.includes(weekDay));
  const {
    data: status,
    isLoading: isStatusLoading,
    error: statusError,
  } = useLunchStatus(date, isLunchEnabled && isWorkDay, isWorkDay);
  const menus = status?.MenuList ?? [];
  const isOrderingClosed = status && !status.ShowLunchSubmitButton && !status.ShowLunchCancelButton;
  const existingRequestId = status?.ShowLunchCancelButton ? status.FoodRequestId : '';

  // Auto-select when only one menu is available
  useEffect(() => {
    if (menus.length === 1 && !selectedMenuId && !existingRequestId) {
      setSelectedMenuId(menus[0].ItemId);
    }
  }, [menus, selectedMenuId, existingRequestId]);

  const { mutateAsync: requestLunch, isPending: isRequesting } = useRequestLunch();
  const { mutateAsync: cancelLunchRequest, isPending: isCancelling } = useCancelLunchRequest();

  const handleSubmit = async () => {
    if (!selectedMenuId) return;
    try {
      const result = await requestLunch(selectedMenuId);
      if (result.IsSuccessful) setSelectedMenuId('');
    } catch {
      // Error toast is already shown by mutation global error handler.
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTargetId) return;
    await cancelLunchRequest(cancelTargetId);
    setCancelTargetId(null);
  };

  // Formatted date label: "Thursday, Nov 5"
  const formattedDate = useMemo(() => {
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return date;
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  }, [date]);

  const deadlineText = `11:00 AM on ${formattedDate}`;

  if (isWorkDaysLoading || isStatusLoading) {
    return <RequestTabSkeleton />;
  }

  // State 1: Lunch not enabled for user
  if (!isLunchEnabled) {
    return (
      <div className="flex flex-col gap-6">
        <MealHero
          eyebrow="Lunch"
          title={t('NOT_PERMITTED_FOR_LUNCH', { defaultValue: "Lunch isn't turned on for you" })}
          description={t('ASK_HR_FOR_LUNCH', {
            defaultValue: 'Ask HR to turn on lunch in your profile.',
          })}
          showClock={false}
        />
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground shadow-sm">
          <Lock className="size-5 shrink-0" />
          <span>{t('ASK_HR_FOR_LUNCH', { defaultValue: 'Ask HR to turn on lunch in your profile.' })}</span>
        </div>
      </div>
    );
  }

  // State 2: Weekend / Not a work day
  if (!isWorkDay) {
    return (
      <div className="flex flex-col gap-6">
        <MealHero
          eyebrow={`Lunch · ${weekDay}`}
          title={`${weekDay} is a weekend, so there's no lunch`}
          description={t('NO_LUNCH_ON_WEEKENDS', {
            defaultValue: 'Friday and Saturday have no lunch.',
          })}
          showClock={false}
        />
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground shadow-sm">
          <Calendar className="size-5 shrink-0" />
          <span>
            {t('NOT_A_WORK_DAY_FOR_LUNCH', {
              defaultValue: `${weekDay} is not a scheduled work day for lunch orders.`,
            })}
          </span>
        </div>
      </div>
    );
  }

  // State 3: Error loading data
  if (statusError) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive">
        {t('ERROR_LOADING_DATA', { defaultValue: 'Error loading data:' })} {statusError.message}
      </div>
    );
  }

  // State 4: Already ordered
  if (existingRequestId && status) {
    const orderedMenu = {
      ItemId: status.FoodRequestId || 'ordered-menu',
      Name: status.MenuName || 'Ordered Menu',
      WeekDay: weekDay,
      MenuItems: status.RequestedMenu || [],
      IsActive: true,
    };

    return (
      <div className="flex flex-col gap-6">
        <MealHero
          eyebrow={`Lunch · ${weekDay}`}
          title={`You've ordered ${status.MenuName || 'lunch'} for ${formattedDate}`}
          description={`${status.RequestedMenu?.join(', ') || ''}. You can cancel until ${deadlineText}.`}
          isDone={true}
          showClock={true}
          targetDate={date}
        />

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <MealOptionCard
                menu={orderedMenu}
                index={0}
                isSelected={true}
                isOrdered={true}
                showRadio={false}
              />
            </div>

            <MealBar
              isOrdered={true}
              deadlineText={deadlineText}
              onCancel={() => setCancelTargetId(existingRequestId)}
              isCancelling={isCancelling}
              canCancel={canCancel}
            />
          </div>

          <aside aria-label="Lunch this week" className="w-full">
            <MealWeek targetDate={date} userRequests={userRequests} />
          </aside>
        </div>

        <ConfirmationModal
          open={Boolean(cancelTargetId)}
          onOpenChange={(open) => !open && setCancelTargetId(null)}
          title={t('CANCEL_LUNCH_REQUEST')}
          description={t('ARE_YOU_SURE_WANT_TO_CANCEL')}
          confirmText={isCancelling ? t('CANCELLING') : t('CANCEL_REQUEST')}
          preventAutoClose={isCancelling}
          onConfirm={handleCancelConfirm}
        />
      </div>
    );
  }

  // State 5: No active menus configured for this day
  if (menus.length === 0 || status?.MessageKey === 'LUNCH_NO_ACTIVE_MENU') {
    return (
      <div className="flex flex-col gap-6">
        <MealHero
          eyebrow={`Lunch · ${weekDay}`}
          title={`No lunch menu for ${formattedDate} yet`}
          description={t('NO_ACTIVE_MENU_HINT', {
            defaultValue: 'Check back later. Menus appear here once HR sets them.',
          })}
          showClock={false}
        />
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground shadow-sm">
          <Utensils className="size-5 shrink-0 opacity-60" />
          <span>
            {t('NO_ACTIVE_MENU', { defaultValue: 'No active menu configured for this day.' })}
          </span>
        </div>
      </div>
    );
  }

  // State 6: Other closed reason (e.g., weekend or custom cutoff)
  if (isOrderingClosed) {
    return (
      <div className="flex flex-col gap-6">
        <MealHero
          eyebrow={`Lunch · ${weekDay}`}
          title={t('ORDERING_CLOSED', { defaultValue: 'Ordering is closed' })}
          description={status?.MessageKey ? t(status.MessageKey, { ...status.MessageParams }) : ''}
          showClock={false}
        />
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground shadow-sm">
          <Utensils className="size-5 shrink-0 opacity-60" />
          <span>{status?.MessageKey ? t(status.MessageKey, { ...status.MessageParams }) : ''}</span>
        </div>
      </div>
    );
  }

  // State 7: Active menus available for ordering
  const isSolo = menus.length === 1;
  const chosenMenu = menus.find((m) => m.ItemId === selectedMenuId) || (isSolo ? menus[0] : null);

  return (
    <div className="flex flex-col gap-6">
      <MealHero
        eyebrow={`Lunch · ${weekDay}`}
        title={
          isSolo
            ? `${menus[0].Name} is on for ${formattedDate}`
            : `What's for lunch on ${formattedDate}?`
        }
        description={
          isSolo
            ? `It's the only menu today. Order by ${deadlineText}, or skip if you're eating out.`
            : `Pick one of ${menus.length} menus. You can change or cancel until ${deadlineText}.`
        }
        isDone={false}
        showClock={true}
        targetDate={date}
      />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <RadioGroup
            value={selectedMenuId}
            onValueChange={setSelectedMenuId}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {menus.map((menu, index) => (
              <MealOptionCard
                key={menu.ItemId}
                menu={menu}
                index={index}
                isSelected={selectedMenuId === menu.ItemId}
                onSelect={() => setSelectedMenuId(menu.ItemId)}
              />
            ))}

            {isSolo && <MealSlot menuName={menus[0].Name} weekDay={weekDay} />}
          </RadioGroup>

          <MealBar
            isOrdered={false}
            selectedMenu={chosenMenu}
            targetDayLabel={formattedDate}
            onOrder={handleSubmit}
            isOrdering={isRequesting}
            canRequest={canRequest}
          />
        </div>

        <aside aria-label="Lunch this week" className="w-full">
          <MealWeek targetDate={date} userRequests={userRequests} />
        </aside>
      </div>

      <ConfirmationModal
        open={Boolean(cancelTargetId)}
        onOpenChange={(open) => !open && setCancelTargetId(null)}
        title={t('CANCEL_LUNCH_REQUEST')}
        description={t('ARE_YOU_SURE_WANT_TO_CANCEL')}
        confirmText={isCancelling ? t('CANCELLING') : t('CANCEL_REQUEST')}
        preventAutoClose={isCancelling}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
};

const HistoryTab = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MY_LUNCH_REQUESTS_ORDER,
  });

  const { data, isLoading, error } = useGetMyLunchRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({ UserId: userId }, columnWhere),
    order,
  });
  const result = (data as { LunchRequests?: LunchRequestsPagingResult })?.LunchRequests;
  const requests = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = useMemo(() => createRequestHistoryTableColumns({ t }), [t]);

  return (
    <AdvanceDataTable
      {...tableProps}
      data={requests}
      columns={columns}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      pagination={{
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        totalCount,
      }}
      isExpandRowContent={false}
      tableClassName="min-w-[28rem]"
      mobileView="cards"
    />
  );
};

/**
 * Order Meal (HR Module Navigation Map; was Request Lunch): tabs Lunch / Dinner (Soon) / History in the
 * URL (`?tab=`; the old `request` tab opens Lunch).
 */
export const RequestLunchPage = () => {
  const { t } = useTranslation();
  const date = useMemo(() => getTargetLunchDay(), []);
  const userId = useCurrentUserId();

  const [tab, setTab] = useUrlTab<MealTab>(
    [MEAL_TABS.LUNCH, MEAL_TABS.DINNER, MEAL_TABS.HISTORY],
    MEAL_TABS.LUNCH,
    { request: MEAL_TABS.LUNCH }
  );

  // Fetch recent user requests to power the checkmarks on "This week"
  const { data: userRequestsData } = useGetMyLunchRequests({
    pageNo: 1,
    pageSize: 20,
    filter: mergeWhere({ UserId: userId }),
    order: DEFAULT_MY_LUNCH_REQUESTS_ORDER,
  });
  const userRequests =
    (userRequestsData as { LunchRequests?: LunchRequestsPagingResult })?.LunchRequests?.items ?? [];

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('ORDER_MEAL')} />

      <Tabs value={tab} onValueChange={(value) => setTab(value as MealTab)}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value={MEAL_TABS.LUNCH} className="flex-1 sm:flex-none">
            {t('LUNCH')}
          </TabsTrigger>
          <TabsTrigger value={MEAL_TABS.DINNER} className="flex-1 gap-1.5 sm:flex-none">
            {t('DINNER')}
            <SoonBadge />
          </TabsTrigger>
          <TabsTrigger value={MEAL_TABS.HISTORY} className="flex-1 sm:flex-none">
            {t('HISTORY')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={MEAL_TABS.LUNCH} className="mt-4">
          <RequestTab date={date} userRequests={userRequests} />
        </TabsContent>
        <TabsContent value={MEAL_TABS.DINNER} className="mt-4">
          <SoonPanel name="DINNER" />
        </TabsContent>
        <TabsContent value={MEAL_TABS.HISTORY} className="mt-4">
          <HistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
